import React, { useState, useEffect } from 'react';
import { Area, AreaChart, CartesianGrid, XAxis } from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import Select from '@/components/ui/Select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { useIsMobile } from '../../hooks/use-mobile';

// Sample data - you can replace this with real data from your analytics
const generateChartData = () => {
  const data = [];
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 90);
  
  for (let i = 0; i < 90; i++) {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);
    
    data.push({
      date: date.toISOString().split('T')[0],
      visitors: Math.floor(Math.random() * 500) + 100,
      users: Math.floor(Math.random() * 300) + 50,
      pageViews: Math.floor(Math.random() * 800) + 200,
    });
  }
  
  return data;
};

const chartData = generateChartData();

const chartConfig = {
  visitors: {
    label: "Visitors",
    color: "var(--color-primary)",
  },
  users: {
    label: "Users", 
    color: "var(--color-accent)",
  },
  pageViews: {
    label: "Page Views",
    color: "var(--color-success)",
  },
};

export function EnhancedChartArea() {
  const { currentColors } = useAdminTheme();
  const isMobile = useIsMobile();
  const [timeRange, setTimeRange] = useState("90d");
  const [selectedMetric, setSelectedMetric] = useState("visitors");

  useEffect(() => {
    if (isMobile) {
      setTimeRange("7d");
    }
  }, [isMobile]);

  const filteredData = chartData.filter((item) => {
    const date = new Date(item.date);
    const referenceDate = new Date();
    let daysToSubtract = 90;
    
    if (timeRange === "30d") {
      daysToSubtract = 30;
    } else if (timeRange === "7d") {
      daysToSubtract = 7;
    }
    
    const startDate = new Date(referenceDate);
    startDate.setDate(startDate.getDate() - daysToSubtract);
    return date >= startDate;
  });

  // Calculate totals for display
  const totalVisitors = filteredData.reduce((sum, item) => sum + item.visitors, 0);
  const avgDaily = Math.round(totalVisitors / filteredData.length);

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-col space-y-1.5 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div>
          <CardTitle className="text-lg font-semibold">Analytics Overview</CardTitle>
          <CardDescription>
            <span className="hidden sm:block">
              Total for the last {timeRange === "90d" ? "3 months" : timeRange === "30d" ? "30 days" : "7 days"}
            </span>
            <span className="sm:hidden">
              Last {timeRange === "90d" ? "3 months" : timeRange === "30d" ? "30 days" : "7 days"}
            </span>
          </CardDescription>
        </div>
        
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          {/* Metric Selector */}
          <Select 
            value={selectedMetric} 
            onChange={setSelectedMetric}
            options={[
              { value: "visitors", label: "Visitors" },
              { value: "users", label: "Users" },
              { value: "pageViews", label: "Page Views" }
            ]}
            className="w-32"
          />
          
          {/* Time Range Selector */}
          <ToggleGroup
            type="single"
            value={timeRange}
            onValueChange={setTimeRange}
            variant="outline"
            className="hidden sm:flex"
          >
            <ToggleGroupItem value="90d" className="text-xs">3M</ToggleGroupItem>
            <ToggleGroupItem value="30d" className="text-xs">30D</ToggleGroupItem>
            <ToggleGroupItem value="7d" className="text-xs">7D</ToggleGroupItem>
          </ToggleGroup>
          
          <Select 
            value={timeRange} 
            onChange={setTimeRange}
            options={[
              { value: "90d", label: "3 months" },
              { value: "30d", label: "30 days" },
              { value: "7d", label: "7 days" }
            ]}
            className="w-28 sm:hidden"
          />
        </div>
      </CardHeader>
      
      <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
        {/* Stats Summary */}
        <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div className="text-center">
            <div className="text-2xl font-bold" style={{ color: currentColors.text }}>
              {totalVisitors.toLocaleString()}
            </div>
            <div className="text-xs" style={{ color: currentColors.textMuted }}>
              Total {selectedMetric}
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold" style={{ color: currentColors.text }}>
              {avgDaily.toLocaleString()}
            </div>
            <div className="text-xs" style={{ color: currentColors.textMuted }}>
              Daily Average
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold" style={{ color: currentColors.success }}>
              +12.5%
            </div>
            <div className="text-xs" style={{ color: currentColors.textMuted }}>
              Growth Rate
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold" style={{ color: currentColors.primary }}>
              {filteredData.length}
            </div>
            <div className="text-xs" style={{ color: currentColors.textMuted }}>
              Days Tracked
            </div>
          </div>
        </div>
        
        {/* Chart */}
        <ChartContainer config={chartConfig} className="aspect-auto h-[300px] w-full">
          <AreaChart data={filteredData} margin={{ left: 0, right: 0, top: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="fillGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="5%"
                    stopColor={currentColors.primary}
                    stopOpacity={0.8}
                  />
                  <stop
                    offset="95%"
                    stopColor={currentColors.primary}
                    stopOpacity={0.1}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={currentColors.border} opacity={0.3} />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={32}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return date.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  });
                }}
                tick={{ fill: currentColors.textMuted, fontSize: 12 }}
              />
              <ChartTooltip
                cursor={{ stroke: currentColors.primary, strokeWidth: 1 }}
                content={
                  <ChartTooltipContent
                    labelFormatter={(value) => {
                      return new Date(value).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      });
                    }}
                    indicator="dot"
                  />
                }
              />
              <Area
                dataKey={selectedMetric}
                type="monotone"
                fill="url(#fillGradient)"
                stroke={currentColors.primary}
                strokeWidth={2}
                dot={false}
                activeDot={{
                  r: 4,
                  fill: currentColors.primary,
                  stroke: currentColors.background,
                  strokeWidth: 2,
                }}
              />
            </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
